<template>
  <div class="brain-index-page">
    <div
      v-if="contractBreadcrumbName"
      class="contract-breadcrumb"
    >
      <span class="breadcrumb-label">{{ t('common.labels.contractBreadcrumbLabel') }} :</span>
      <router-link
        v-if="contractBreadcrumbLink"
        class="breadcrumb-value"
        :to="contractBreadcrumbLink"
      >
        {{ contractBreadcrumbName }}
      </router-link>
      <span
        v-else
        class="breadcrumb-value"
      >
        {{ contractBreadcrumbName }}
      </span>
    </div>

    <div
      v-if="!requestId"
      class="page-header"
    >
      <h1 class="page-title">
        <v-icon size="32" color="#EA5545" class="mr-2">mdi-brain</v-icon>
       Frag Clearment.AI - die KI für Deinen Vertrag
      </h1>
      <p class="page-subtitle">
        Übersicht deiner Clearment.AI Chats. Die Antworten basieren auf den Angaben in dem von der Versicherung ausgefüllten Clearment Klarvertrags.
      </p>
    </div>

    <div
      v-if="showInlineSearch"
      class="brain-inline-search mb-4"
    >
      <BrainSearch
        :full-width="true"
        :show-history="false"
        :preset-product-id="productId ?? undefined"
        :lock-product-selection="productId !== null"
        @search-completed="handleBrainSearchCompleted"
      />
    </div>

    <!-- Liste / Übersicht (abhängig von Route-Parametern) -->
    <div v-if="!requestId">
      <div v-if="isLoading" class="state-block" data-state="loading">
        <v-progress-circular indeterminate color="#EA5545" size="64"></v-progress-circular>
        <p class="state-text">Clearment.AI Chats werden geladen ...</p>
      </div>

      <div v-else-if="requests.length > 0" class="requests-list mt-4">
        <h1 class="page-title">
        <v-icon size="32" color="#EA5545" class="mr-2">mdi-brain</v-icon>
        Deine Clearment.AI Chats
      </h1>
  
        <v-card
          v-for="request in requests"
          :key="request.breq_id"
          class="request-card"
          elevation="1"
          @click="goToRequest(request.breq_id)"
          >
          <v-card-text>
            <div class="request-header">
              <div class="request-header-left">
                <!-- <v-chip
                  size="small"
                  :color="getStatusColor(request.breq_status)"
                  variant="tonal"
                  class="mr-2"
                >
                  {{ getStatusText(request.breq_status) }}
                </v-chip> -->
                <v-chip
                  v-if="request.prod_name"
                  size="small"
                  color="#EA5545"
                  variant="outlined"
                >
                  <v-icon size="14" class="mr-1">mdi-package-variant</v-icon>
                  {{ request.prod_name }}
                </v-chip>
              </div>
              <div class="request-date">
                <v-icon size="16" color="#666" class="mr-1">mdi-clock-outline</v-icon>
                <span>{{ formatDate(request.breq_created_date) }}</span>
              </div>
            </div>

            <div class="request-body">
              <div class="request-prompt">
                {{ truncateText(request.breq_prompt, 160) }}
              </div>
              <div class="request-meta">
                <span v-if="request.breq_total_tokens">
                  Tokens: {{ request.breq_total_tokens }}
                </span>
                <span v-if="formatCost(request.breq_total_cost)">
                  · Kosten: {{ formatCost(request.breq_total_cost) }} $
                </span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <div v-else class="state-block" data-state="empty">
        <v-icon size="80" color="#ccc">mdi-brain</v-icon>
        <h3 class="state-title">Noch keine Clearment.AI Chats</h3>
        <p class="state-text">
          Stelle deine erste Frage über die Clearment.AI Chat-Suche, um hier Einträge zu sehen.
        </p>
      </div>
    </div>

    <!-- Detailansicht eines Requests (mit ID) -->
    <div v-else class="request-detail-view">
      <v-btn
        variant="text"
        class="back-btn mb-4"
        @click="goBackToList"
      >
        <v-icon left>mdi-arrow-left</v-icon>
        alle Clearment.AI Chats anzeigen
      </v-btn>

      <div v-if="isLoading" class="state-block" data-state="loading">
        <v-progress-circular indeterminate color="#EA5545" size="64"></v-progress-circular>
      </div>

      <v-card v-else-if="currentRequest" class="detail-card" elevation="1">
        <v-card-text>
          <div class="detail-header">
            <div class="detail-header-main">
              <div class="detail-header-top">
           
                <div
                  v-if="currentRequest.prod_name"
                  class="detail-product-chip"
                >
                  <router-link
                    v-if="detailProductLink"
                    :to="detailProductLink"
                    class="product-chip-link"
                  >
                    <v-icon size="16" class="mr-1">mdi-package</v-icon>
                    {{ currentRequest.prod_name }}
                  </router-link>
                  <div v-else class="product-chip-link">
                    <v-icon size="16" class="mr-1">mdi-package</v-icon>
                    {{ currentRequest.prod_name }}
                  </div>
                </div>
              </div>
              <h2 class="detail-title">
                <v-icon size="22" color="#EA5545" class="mr-2">mdi-brain</v-icon>
                Clearment.AI  #{{ currentRequest.breq_id }}
              </h2>
              <div class="detail-meta">
                <span class="meta-item">
                  <v-icon size="16" color="#666" class="mr-1">mdi-clock-outline</v-icon>
                  {{ formatDate(currentRequest.breq_created_date) }}
                </span>
              </div>
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <div class="detail-section">
            <h3 class="section-title">Deine Frage</h3>
            <p class="section-text">
              {{ currentRequest.breq_prompt }}
            </p>
          </div>

          <div class="detail-section" v-if="currentRequest.breq_result">
            <h3 class="section-title">
              <v-icon size="18" color="#EA5545" class="mr-1">mdi-brain</v-icon>
              Clearment.AI Antwort
            </h3>
            <div
              class="section-text answer-text"
              v-html="formatAnswer(currentRequest.breq_result)"
            ></div>
          </div>

          <div class="detail-section" v-if="currentRequest.breq_error">
            <h3 class="section-title error-title">
              <v-icon size="18" color="#f44336" class="mr-1">mdi-alert-circle</v-icon>
              Fehler
            </h3>
            <p class="section-text error-text">
              {{ currentRequest.breq_error }}
            </p>
          </div>

          <div class="detail-section meta-section">
            <h3 class="section-title">Meta</h3>
            <p class="section-text small-text">
              Tokens: {{ currentRequest.breq_total_tokens ?? 0 }}
              · Input: {{ currentRequest.breq_input_tokens ?? 0 }}
              · Output: {{ currentRequest.breq_output_tokens ?? 0 }}
              <span v-if="formatCost(currentRequest.breq_total_cost)">
                · Kosten: {{ formatCost(currentRequest.breq_total_cost) }} $
              </span>
            </p>
          </div>
        </v-card-text>
      </v-card>

      <div v-else class="state-block" data-state="empty">
        <v-icon size="80" color="#ccc">mdi-brain</v-icon>
        <h3 class="state-title">Request nicht gefunden</h3>
        <p class="state-text">
          Dieser Clearment.AI Chat existiert nicht oder gehört nicht zu deinem Account.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BrainSearch from '@/components/objects/BrainSearch.vue'
import { useLanguage } from '@/composables/useLanguage'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const { t } = useLanguage()
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

const route = useRoute()
const router = useRouter()

const requests = ref([])
const currentRequest = ref(null)
const isLoading = ref(false)
const contractInfo = ref(null)
const knownContracts = ref([])
const contractsLoaded = ref(false)

const productId = computed(() => {
  const raw = route.params.productid
  if (!raw && raw !== 0) return null
  const num = Number(raw)
  return Number.isNaN(num) ? null : num
})

const currentProductSupportsBrainSearch = computed(() => {
  if (!productId.value) return true
  if (!contractInfo.value) return true
  return Number(contractInfo.value?.prod_brain_search || 0) === 1
})

const detailProductLink = computed(() => {
  if (!currentRequest.value) return null
  const contractId = currentRequest.value.contract_myp_id
  if (contractId) {
    return { name: 'vertrag', params: { id: contractId } }
  }
  const prodId = currentRequest.value.breq_product_id
  if (prodId) {
    return { name: 'brain', params: { productid: prodId } }
  }
  return null
})

const requestId = computed(() => {
  const raw = route.params.id
  if (!raw && raw !== 0) return null
  const num = Number(raw)
  return Number.isNaN(num) ? null : num
})

const showInlineSearch = computed(() => !requestId.value && currentProductSupportsBrainSearch.value)
const contractBreadcrumbName = computed(() => {
  if (!contractInfo.value) return ''
  return (
    contractInfo.value.prod_name ||
    contractInfo.value.user_name ||
    (contractInfo.value.myp_id ? `#${contractInfo.value.myp_id}` : '')
  )
})

const contractBreadcrumbLink = computed(() => {
  if (!contractInfo.value || !contractInfo.value.myp_id) return null
  return {
    name: 'vertrag',
    params: { id: contractInfo.value.myp_id }
  }
})

// function getStatusColor(status) {
//   const colors = {
//     0: 'orange',   // Pending
//     1: 'green',    // Success
//     2: 'red'       // Error
//   }
//   return colors[status] || 'grey'
// }

// function getStatusClass(status) {
//   const classes = {
//     0: 'status-pill--pending',
//     1: 'status-pill--success',
//     2: 'status-pill--error'
//   }
//   return classes[status] || 'status-pill--unknown'
// }

// function getStatusText(status) {
//   const texts = {
//     0: 'In Bearbeitung',
//     1: 'Abgeschlossen',
//     2: 'Fehler'
//   }
//   return texts[status] || 'Unbekannt'
// }

function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

function formatAnswer(answer) {
  if (!answer) return ''
  return answer.replace(/\n/g, '<br>')
}

function formatCost(value) {
  if (value === null || value === undefined) return ''
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return ''
  return num.toFixed(6)
}

async function loadAllRequests() {
  isLoading.value = true
  currentRequest.value = null
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getmybrainrequests'
    })
    if (response.data.success && response.data.requests) {
      requests.value = response.data.requests
    } else {
      requests.value = []
    }
  } catch (err) {
    console.error('Error loading brain requests:', err)
    errorStore.setError(true, 'Fehler beim Laden der Clearment.AI Chats')
    requests.value = []
  } finally {
    isLoading.value = false
  }
}

async function loadRequestsForProduct(prodId) {
  isLoading.value = true
  currentRequest.value = null
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getmybrainrequests'
    })
    if (response.data.success && response.data.requests) {
      const all = response.data.requests
      requests.value = all.filter(r => Number(r.breq_product_id) === Number(prodId))
      ensureContractInfo(prodId, requests.value[0]?.prod_name)
    } else {
      requests.value = []
    }
  } catch (err) {
    console.error('Error loading product-specific brain requests:', err)
    errorStore.setError(true, 'Fehler beim Laden der Clearment.AI Chats für dieses Produkt')
    requests.value = []
  } finally {
    isLoading.value = false
  }
}

async function handleBrainSearchCompleted() {
  if (requestId.value) {
    return
  }
  if (productId.value !== null && productId.value !== undefined) {
    await loadRequestsForProduct(productId.value)
  } else {
    await loadAllRequests()
  }
}

async function loadSingleRequest(id) {
  isLoading.value = true
  currentRequest.value = null
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getbrainrequest',
      breq_id: id
    })
    if (response.data.success && response.data.request) {
      currentRequest.value = response.data.request
      const contractId =
        currentRequest.value.contract_myp_id ||
        currentRequest.value.breq_product_id ||
        productId.value
      if (contractId) {
        ensureContractInfo(contractId, currentRequest.value.prod_name)
      }
    } else {
      currentRequest.value = null
      if (response.data.message) {
        errorStore.setError(true, response.data.message)
      }
    }
  } catch (err) {
    console.error('Error loading brain request:', err)
    errorStore.setError(true, 'Fehler beim Laden des Clearment.AI Chats')
    currentRequest.value = null
  } finally {
    isLoading.value = false
  }
}

function goToRequest(id) {
  // Wenn ein Produkt-Kontext existiert, beibehalten
  if (productId.value) {
    router.push({ name: 'brain', params: { productid: productId.value, id } })
  } else {
    router.push({ name: 'brain', params: { id } })
  }
}

function goBackToList() {
  if (currentRequest.value && currentRequest.value.breq_product_id) {
    router.push({
      name: 'brain',
      params: { productid: currentRequest.value.breq_product_id }
    })
  } else if (productId.value) {
    router.push({ name: 'brain', params: { productid: productId.value } })
  } else {
    router.push({ name: 'brain' })
  }
}

async function loadAccordingToRoute() {
  if (requestId.value) {
    await loadSingleRequest(requestId.value)
  } else if (productId.value) {
    await loadRequestsForProduct(productId.value)
  } else {
    await loadAllRequests()
    contractInfo.value = null
  }
}

async function ensureContractsLoaded() {
  if (contractsLoaded.value) return
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getmyproducts'
    })
    if (response.data.success && response.data.products) {
      knownContracts.value = response.data.products
    } else {
      knownContracts.value = []
    }
  } catch (err) {
    console.error('Error loading contracts for breadcrumb:', err)
    knownContracts.value = []
  } finally {
    contractsLoaded.value = true
  }
}

async function ensureContractInfo(mypId, fallbackName = '') {
  if (!mypId) {
    contractInfo.value = null
    return
  }
  if (
    contractInfo.value &&
    Number(contractInfo.value.myp_id) === Number(mypId)
  ) {
    if (!contractInfo.value.prod_name && fallbackName) {
      contractInfo.value = {
        ...contractInfo.value,
        prod_name: fallbackName
      }
    }
    return
  }

  await ensureContractsLoaded()
  const match = knownContracts.value.find(
    (contract) => Number(contract.myp_id) === Number(mypId)
  )
  if (match) {
    contractInfo.value = match
  } else {
    contractInfo.value = {
      myp_id: mypId,
      prod_name: fallbackName
    }
  }
}

onMounted(loadAccordingToRoute)

watch(
  () => ({ productid: route.params.productid, id: route.params.id }),
  () => {
    loadAccordingToRoute()
  }
)

watch(
  () => productId.value,
  (newVal) => {
    if (!newVal) {
      if (!requestId.value) {
        contractInfo.value = null
      }
      return
    }
    ensureContractInfo(newVal)
  }
)
</script>

<style scoped src="@/assets/styles/sfc/components/sites/BrainIndex.css"></style>
